/**
 * Product quote modal — open/close + submit with inline success state (no page
 * reload). Posts JSON to our own ops endpoint (POST /leads, source=product_quote)
 * and shows an honest success/error from the real response — no Shopify /contact,
 * no native form navigation. On success shows "Inquiry received!" and auto-closes
 * after 5s.
 *
 * WHY A STANDALONE ASSET (not inline in sections/product-main.liquid):
 * the section's JS lives in one big {% javascript %} block that Shopify
 * concatenates with every other section's JS into a single bundle. A single
 * fault anywhere in that bundle disables ALL section JS — which would stop
 * this form's submit interception from attaching and let the contact form
 * submit natively, page-redirecting to ?contact_posted=true (the success
 * state never shows). Living in its own file (loaded `defer` from
 * snippets/product-quote-modal.liquid, same pattern as assets/lead-form.js)
 * makes the interception immune to faults elsewhere. It is also decoupled
 * from the [data-product-form] iteration it used to be nested inside.
 *
 * The form is not in the page until the first open (2026-10-29): it ships in a
 * <template>, and buildForm() clones it in, fills what other scripts used to
 * fill at page load (referral code, phone field, the selected variant), and
 * only then loads Cloudflare Turnstile for it. That keeps ~950 KB of Turnstile
 * and a few hundred nodes off every product page — docs/plans/lighthouse-2026-10.md.
 *
 * The variant→quote-card sync (image / variant / price + hidden context
 * inputs) stays in sections/product-main.liquid's variant-change handler. It
 * looks the card up on every call and publishes window.bqSyncQuoteCard, which
 * buildForm() calls so a variant picked before the first open is not lost.
 */
(function () {
  function initQuoteModal() {
    var modal = document.querySelector('[data-product-quote-modal]');
    if (!modal || typeof modal.showModal !== 'function') return;

    // Set by buildForm() on the first open — until then the form is template content.
    var quoteForm = null;
    var successEl = modal.querySelector('[data-quote-success]');
    var errorEl = modal.querySelector('[data-quote-error]');
    var retryBtn = modal.querySelector('[data-quote-retry]');
    var leadsUrl = modal.dataset.leadsUrl || '/leads';
    var turnstileRequired = modal.dataset.turnstileRequired === 'true';
    var autoCloseTimer = null;

    function submitBtnEl() {
      return quoteForm && quoteForm.querySelector('[data-quote-submit]');
    }

    // Ground truth for "is Turnstile satisfied" — never a JS boolean, which is
    // lost if CF's verify callback fires before this deferred script defines it.
    // Shared implementation: widget API first, hidden input as fallback.
    function turnstileToken() {
      return quoteForm ? window.bqLeadToken(quoteForm) : '';
    }

    // Clear the notice as soon as the widget catches up. Nothing is gated on this —
    // the submit button is never disabled waiting for Turnstile, because a lost
    // callback then leaves it dead forever under a green checkmark. The token is
    // checked at submit time instead. See the contract in assets/lead-submit.js.
    function syncTurnstileHint() {
      if (quoteForm && turnstileToken()) window.bqLeadHint(quoteForm, false);
    }

    // The widget lives inside a closed <dialog> and only verifies once shown, so the
    // notice can linger after it succeeds. Poll for as long as the modal is open —
    // purely cosmetic now, nothing depends on it. Stopped on close.
    var turnstilePollId = null;
    function stopTurnstilePoll() {
      if (turnstilePollId) { window.clearInterval(turnstilePollId); turnstilePollId = null; }
    }
    function pollTurnstileUntilVerified() {
      if (!turnstileRequired) return;
      stopTurnstilePoll();
      turnstilePollId = window.setInterval(function () {
        syncTurnstileHint();
        if (turnstileToken() !== '') stopTurnstilePoll();
      }, 250);
    }

    // Cloudflare Turnstile callbacks — must be global so the CF script can find them
    // by name (set in data-callback attrs).
    window.onQuoteTurnstileVerify = syncTurnstileHint;
    window.onQuoteTurnstileExpired = syncTurnstileHint;
    window.onQuoteTurnstileError = syncTurnstileHint;

    // Explicit render: the widget only exists once the form is built, and implicit mode
    // scans the page once, at script load. The params mirror the div's data attributes.
    function renderTurnstile() {
      var widget = quoteForm && quoteForm.querySelector('.cf-turnstile');
      if (!widget || widget.dataset.turnstileRendered === '1') return;
      if (!window.turnstile || typeof window.turnstile.render !== 'function') return;
      widget.dataset.turnstileRendered = '1';
      window.turnstile.render(widget, {
        sitekey: widget.dataset.sitekey,
        theme: widget.dataset.theme || 'light',
        size: widget.dataset.size || 'normal',
        callback: syncTurnstileHint,
        'expired-callback': syncTurnstileHint,
        'error-callback': syncTurnstileHint,
      });
    }

    function loadTurnstile() {
      if (!turnstileRequired) return;
      if (window.turnstile && typeof window.turnstile.render === 'function') {
        renderTurnstile();
        return;
      }
      if (document.querySelector('script[data-quote-turnstile]')) return;
      window.bqQuoteTurnstileLoaded = renderTurnstile;
      var script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=bqQuoteTurnstileLoaded';
      script.async = true;
      script.setAttribute('data-quote-turnstile', '');
      document.head.appendChild(script);
    }

    // Clone the form out of its <template>, once. Everything a page-load script would have
    // done to it is done here instead, because those scripts ran before it existed.
    function buildForm() {
      if (quoteForm) return quoteForm;
      var tpl = modal.querySelector('template[data-quote-form-template]');
      if (tpl) {
        tpl.parentNode.insertBefore(tpl.content.cloneNode(true), tpl);
        tpl.parentNode.removeChild(tpl);
      }
      // Use class selector — selecting by class avoids missing the element and
      // letting the form submit natively (which navigates the page).
      quoteForm = modal.querySelector('.quote-modal__form');
      if (!quoteForm) {
        console.warn('[product-quote-modal] form element not found inside dialog — quote modal disabled');
        return null;
      }
      var ref = typeof window.bqGetRef === 'function' ? window.bqGetRef() : '';
      if (ref) {
        var refFields = quoteForm.querySelectorAll('[data-lead-ref]');
        for (var i = 0; i < refFields.length; i++) refFields[i].value = ref;
      }
      if (typeof window.bqInitPhoneFields === 'function') window.bqInitPhoneFields(quoteForm);
      if (typeof window.bqSyncQuoteCard === 'function') window.bqSyncQuoteCard();
      quoteForm.addEventListener('submit', onSubmit);
      loadTurnstile();
      return quoteForm;
    }

    function resetStates() {
      if (autoCloseTimer) { window.clearTimeout(autoCloseTimer); autoCloseTimer = null; }
      if (successEl) successEl.hidden = true;
      if (errorEl) errorEl.hidden = true;
      if (quoteForm) quoteForm.hidden = false;
      modal.classList.remove('is-submitting');
      // Restore submit button label
      var submitBtn = submitBtnEl();
      if (submitBtn && submitBtn.dataset.labelSubmit) {
        submitBtn.textContent = submitBtn.dataset.labelSubmit;
      }
      // Don't reset the Turnstile widget here. The token stays valid (~5 min) and CF
      // auto-refreshes on expiry. Just clear any notice left from a previous attempt.
      window.bqLeadHint(quoteForm, false);
    }

    function showSuccess() {
      if (quoteForm) quoteForm.hidden = true;
      if (errorEl) errorEl.hidden = true;
      if (successEl) successEl.hidden = false;
      modal.classList.remove('is-submitting');
      // Auto-close after 5s — user can also click X or hit Escape
      // to close earlier.
      autoCloseTimer = window.setTimeout(function () {
        modal.close();
      }, 5000);
    }

    function showError() {
      if (quoteForm) quoteForm.hidden = true;
      if (successEl) successEl.hidden = true;
      if (errorEl) errorEl.hidden = false;
      modal.classList.remove('is-submitting');
    }

    // Open buttons
    document.querySelectorAll('[data-quote-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        buildForm();
        resetStates();
        try { modal.showModal(); } catch (e) { /* already open */ }
        pollTurnstileUntilVerified();
      });
    });

    // Close buttons (header X + footer Close)
    modal.addEventListener('click', function (e) {
      var closeTarget = e.target.closest('[data-quote-close]');
      if (closeTarget) modal.close();
    });

    // Retry button — back to the form
    if (retryBtn) {
      retryBtn.addEventListener('click', resetStates);
    }

    // Backdrop click closes the dialog. We can't rely on
    // `e.target === modal` alone — when content inside the dialog
    // shifts (e.g. Turnstile widget loading async, reflowing the
    // form), a regular content click can momentarily target the
    // dialog element itself before bubbling. Instead, check the
    // click's coordinates against the dialog's bounding rect:
    // outside the rect = backdrop, close. Inside = content, keep.
    modal.addEventListener('click', function (e) {
      if (e.target !== modal) return;
      var rect = modal.getBoundingClientRect();
      var insideContent =
        e.clientX >= rect.left && e.clientX <= rect.right &&
        e.clientY >= rect.top && e.clientY <= rect.bottom;
      if (!insideContent) modal.close();
    });

    // Reset state when the dialog closes (so reopening starts fresh)
    modal.addEventListener('close', function () {
      if (autoCloseTimer) { window.clearTimeout(autoCloseTimer); autoCloseTimer = null; }
      stopTurnstilePoll();
    });

    // Safety net — capture-phase listener at the modal level catches
    // ANY form submit happening inside the dialog before it bubbles
    // and triggers native browser submit. Protects against selector
    // mishaps and ensures we never accidentally page-reload.
    modal.addEventListener('submit', function (e) {
      e.preventDefault();
    }, true);

    function onSubmit(e) {
      e.preventDefault();

      // No token yet: show the reason instead of doing nothing. The widget inside a
      // freshly opened dialog often just hasn't finished — clicking again once it
      // shows its checkmark goes through.
      if (turnstileRequired && !turnstileToken()) {
        window.bqLeadHint(quoteForm, true);
        return;
      }
      window.bqLeadHint(quoteForm, false);

      modal.classList.add('is-submitting');
      var submitBtn = submitBtnEl();
      if (submitBtn && submitBtn.dataset.labelSubmitting) {
        submitBtn.textContent = submitBtn.dataset.labelSubmitting;
      }

      // POST JSON to our own /leads endpoint (real CORS, real response). No Shopify
      // /contact, no native form navigation. The product context (product_title,
      // product_url, variant_id, variant_title) and the shipping fields are plain
      // [data-lead-detail] inputs, so the shared builder picks them up on its own.
      // See assets/lead-submit.js and ops/_system/api/app/schemas/leads.py.
      var payload = window.bqBuildLeadPayload(quoteForm, 'product_quote');

      // A real network failure (DNS/offline) and any non-2xx (rate limit, failed
      // Turnstile, validation) are the same to the visitor: it didn't go through.
      window.bqSubmitLead(leadsUrl, payload).then(function (ok) {
        if (ok) showSuccess(); else showError();
      });
    }
  }

  // Loaded with `defer`, so the DOM is normally parsed by the time this
  // runs — but guard readyState in case the script is ever loaded eagerly.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuoteModal);
  } else {
    initQuoteModal();
  }
})();
